// src/pages/Home/index.jsx
import React, { useEffect, useState, useCallback } from "react";
import QlikCard from "components/QlikCard";
import QlikObject from "components/QlikObject";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { runHomeIntroTour, HOME_TOUR_KEY } from "./introTour";
import { addClassToElement as waitAndAddClass } from "../../utils/addClassToElement";

export default function HomePage({
  title,
  isAppReady,
  appDisclaimer,
  pageTitle,
  ExportChartFootnote,
  menuItemsStrings,
}) {
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    document.title = `${pageTitle} - ${title}`;
  }, [pageTitle, title]);

  console.log(process.env.REACT_APP_PUBLIC_URL);

  /**
   * Prepare all DOM elements that are used as tour targets:
   * waits for each selector and adds the corresponding class (with timeout).
   * Uses the shared util: utils/addClassToElement.js
   */
  const prepareTourTargets = useCallback(async () => {
    const tasks = [
      // Navbar: Home
      waitAndAddClass('li>a[href="#/"]', "tour-global-navbar-homePage"),

      // Navbar: Completed Routes
      waitAndAddClass(
        'li>a[href="#/completedRoutes"]',
        "tour-global-navbar-completedRoutes"
      ),

      // Navbar: Licensing
      waitAndAddClass(
        'li>a[href="#/licensing"]',
        "tour-global-navbar-licensing"
      ),

      // CurrentSelections: front buttons
      waitAndAddClass(
        'div.qv-selections-pager>div[class*="buttons"]',
        "tour-global-current-selections-front-buttons"
      ),

      // CurrentSelections: active filters area
      waitAndAddClass(
        'div.qv-selections-pager>div[class*="qv-pager"]',
        "tour-global-current-selections-active-selections"
      ),

      // KPI "no of routes" switch cell
      waitAndAddClass(
        'td[id^="7b0c1819-964d-48af-afd0-a6acf595b68b_"][id$="_r4c0"]',
        "tour-home-kpi-no-of-routes-switch"
      ),

      // KPI "no of flights" switch cell
      waitAndAddClass(
        'td[id^="apKSxrE_"][id$="_r4c0"]',
        "tour-home-kpi-no-of-flights-switch"
      ),

      // KPI selection button in the barchart header
      waitAndAddClass(
        "button.title-button.1e84a309-130a-4012-8b4e-f3bdbfe66759",
        "tour-home-barchart-kpi-selection"
      ),
    ];

    // We don't want a single failure to block the tour forever → allSettled.
    const results = await Promise.allSettled(tasks);

    // Optional: log which selectors failed (timeout) for debugging.
    results.forEach((res, idx) => {
      if (res.status === "rejected") {
        console.warn(
          "[prepareTourTargets] Failed for task index",
          idx,
          res.reason
        );
      }
    });
  }, []);

  /**
   * Manual start via button:
   * - clear home tour flag
   * - wait for all tour targets to be ready (up to each timeout)
   * - start intro.js tour
   */
  const handleStartTourClick = async () => {
    console.log("Home tour button clicked, resetting the tour...");
    localStorage.removeItem(HOME_TOUR_KEY);

    await prepareTourTargets();
    runHomeIntroTour(menuItemsStrings);
  };

  /**
   * Auto-start on first visit when:
   * - home tour not completed
   * - app ready
   * - desktop layout
   * - URL matches expected
   *
   * No custom loops here: we just call the async helper once.
   */
  useEffect(() => {
    const maybeStartTour = async () => {
      console.log("Current Location:", window.location);

      const hasCompletedTour = localStorage.getItem(HOME_TOUR_KEY);
      const mediaQuery = window.matchMedia("(min-width: 1200px)");

      const isCorrectHref =
        window.location.href === `${process.env.REACT_APP_API_TOUR}/#/`;
      const isEmptySearch = window.location.search === "";

      console.log("isAppReady:", isAppReady);
      console.log("hasCompletedTour:", hasCompletedTour);
      console.log("mediaQuery.matches:", mediaQuery.matches);
      console.log("isCorrectHref:", isCorrectHref);
      console.log("isEmptySearch:", isEmptySearch);

      if (
        !hasCompletedTour &&
        isAppReady &&
        mediaQuery.matches &&
        isCorrectHref &&
        isEmptySearch
      ) {
        await prepareTourTargets();
        runHomeIntroTour(menuItemsStrings);
      }
    };

    maybeStartTour();
  }, [isAppReady, prepareTourTargets, menuItemsStrings]);

  return (
    <section className="content tour-homepage-page">
      <div className="content-header">
        <div className="row mb-2">
          <div className="col-md-10 col-sm-9 col-10">
            <h5 className="page-title"> {title} </h5>
            {/* <h6 className="page-disclaimer"> {appDisclaimer} </h6> */}
          </div>
          <div className="col-md-2 col-sm-3 col-2 d-flex justify-content-end">
            <button
              onClick={handleStartTourClick}
              className="start-tour-button"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {isHovered ? (
                <>
                  {menuItemsStrings.tourParamsTourStartButton}
                  <FontAwesomeIcon icon="circle-play" className="ml-2" />
                </>
              ) : (
                <FontAwesomeIcon icon="circle-play" />
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="container-fluid">
        <div className="row justify-content-start">
          <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12 col-xl-12">
            <div className="row justify-content-left tour-home-kpi-row resize-grid-kpi-row-home">
              {/* KPIs Row */}

              <div className="col-xs-12 col-sm-12 col-md-6 col-lg-2 col-xl-2 tour-home-kpi-no-of-routes">
                {/* KPI: no of routes */}
                <QlikCard
                  id="7b0c1819-964d-48af-afd0-a6acf595b68b"
                  height="120px"
                  showHeader={false}
                  showMaximize={false}
                  showExport={false}
                  showOverlayer={false}
                />
              </div>

              <div className="col-xs-12 col-sm-12 col-md-6 col-lg-2 col-xl-2">
                {/* KPI: no of transporters */}
                <QlikCard
                  id="15f5938b-8d34-45e9-8cec-e60b717508de"
                  height="120px"
                  showHeader={false}
                  showMaximize={false}
                  showExport={false}
                  showOverlayer={false}
                />
              </div>

              <div className="col-xs-12 col-sm-12 col-md-6 col-lg-2 col-xl-2">
                {/* KPI: no of countries */}
                <QlikCard
                  id="9e7dde59-9676-4fa8-8e5a-a28be74d27a8"
                  height="120px"
                  showHeader={false}
                  showMaximize={false}
                  showExport={false}
                  showOverlayer={false}
                />
              </div>

              <div className="col-xs-12 col-sm-12 col-md-6 col-lg-2 col-xl-2">
                {/* KPI: no of destinations */}
                <QlikCard
                  id="1277773f-3c78-4c7c-b0c2-7a8613173047"
                  height="120px"
                  showHeader={false}
                  showMaximize={false}
                  showExport={false}
                  showOverlayer={false}
                />
              </div>

              <div className="col-xs-12 col-sm-12 col-md-6 col-lg-2 col-xl-2 tour-home-kpi-no-of-flights">
                {/* KPI: no of flights */}
                <QlikCard
                  id="apKSxrE"
                  height="120px"
                  showHeader={false}
                  showMaximize={false}
                  showExport={false}
                  showOverlayer={false}
                />
              </div>

{/*               <div className="col-xs-12 col-sm-12 col-md-6 col-lg-2 col-xl-2">
                <QlikCard
                  id="jJTYuxs"
                  height="120px"
                  showHeader={false}
                  showMaximize={false}
                  showExport={false}
                  showOverlayer={false}
                />
              </div> */}

            </div>

            <div className="row justify-content-left">
              {/* Charts Row */}

              <div className="col-xs-12 col-sm-12 col-md-12 col-lg-6 col-xl-6">
                {/* barchart column */}
                <div className="row justify-content-center tour-home-container">
                  <div className="col-12">
                    {/* barchart */}
                    <QlikCard
                      id="49d39e71-7e62-4d01-a6df-a4409a5bff48"
                      height="max(calc(100vh - 263px - 90px - 10px), 150px)"
                      showHeader={true}
                      showMaximize={true}
                      showExport={true}
                      showPdfExport
                      showImageExport={true}
                      exportImageWidth={1280}
                      exportImageHeight={720}
                      onBeforeExportHandler={async ({ vis, exportType }) => {
                        console.log(vis);
                        if (exportType in { pdf: true, image: true } && vis)
                          await vis.setOptions({
                            showTitles: true,
                            footnote: `${ExportChartFootnote}`,
                          });
                      }}
                      onAfterExportHandler={async ({ vis, exportType }) => {
                        console.log("Export type: ", exportType);
                        if (exportType in { pdf: true, image: true })
                          vis.setOptions({ showTitles: false, footnote: "" });
                      }}
                      exportText={menuItemsStrings.exportText}
                      exportCompletedText={
                        menuItemsStrings.exportCompletedText
                      }
                      closeBtnText={menuItemsStrings.closeBtnText}
                    />
                  </div>
                </div>
              </div>

              <div className="col-xs-12 col-sm-12 col-md-12 col-lg-6 col-xl-6">
                {/* container column */}
                <div className="row justify-content-center tour-home-barchart">
                  <div className="col-12">
                    {/* chart container */}
                    <QlikCard
                      id="XYXppS"
                      // height="294px"
                      height="max(calc(100vh - 263px - 90px - 10px), 150px)"
                      showHeader={true}
                      showMaximize={true}
                      showExport={false}
                      showPdfExport
                      showImageExport={true}
                      exportImageWidth={1280}
                      exportImageHeight={720}
                      onBeforeExportHandler={async ({ vis, exportType }) => {
                        console.log(vis);
                      }}
                      onAfterExportHandler={async ({ vis, exportType }) => {
                        console.log("Export type: ", exportType);
                        if (exportType in { pdf: true, image: true })
                          vis.setOptions({ showTitles: false, footnote: "" });
                      }}
                      exportText={menuItemsStrings.exportText}
                      exportCompletedText={
                        menuItemsStrings.exportCompletedText
                      }
                      closeBtnText={menuItemsStrings.closeBtnText}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/*             <div className="col-xs-12 col-sm-12 col-md-12 col-lg-2 col-xl-2 tour-home-filterpane-side">
                  <QlikCard
                    id="GhVZpW"
                    // height="485px"
                    height="max(calc(100vh - 263px - 25px), 100px)"
                    showHeader={false}
                    showMaximize={false}
                    showExport={false}
                    showOverlayer={false}
                  />
            </div> */}
        </div>

        {/*           <div className="row">
            <div className="col-12">
              <QlikObject
                id="AQtTDP"
                height="0px"
                showLoader={false}
                showOverlayer={false}
              />
            </div>
          </div> */}
      </div>
    </section>
  );
}
